import React, { useEffect } from 'react';
import storeBtnEn from '../assets/store-btn-en.png';
import appIcon from '../assets/app-icon.png';
import { Download, Star, CheckCircle, Smile, Image as ImageIcon, Sparkles, Wand2, Type, LayoutGrid, Package, Send } from 'lucide-react';

const StickerMakerLandingEn = () => {
  const PLAY_STORE_LINK = "https://play.google.com/store/apps/details?id=com.global.stickermaker";

  useEffect(() => {
    document.title = "Sticker Maker Pro | The Ultimate WhatsApp Sticker App";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Turn your photos and ideas into funny and professional WhatsApp stickers in seconds. The easiest and most comprehensive app.");
    }
  }, []);

  const handleDownload = () => {
    window.open(PLAY_STORE_LINK, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 font-sans" dir="ltr">
      {/* Navbar */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto bg-transparent">
        <div className="flex items-center gap-3">
          <img src={appIcon} alt="Sticker Maker Pro" className="w-10 h-10 rounded-xl shadow-lg object-cover" />
          <span className="font-bold text-xl text-slate-800">Sticker Maker Pro</span>
        </div>
        <button 
          onClick={handleDownload}
          className="hover:scale-105 transition-transform"
        >
          <img src={storeBtnEn} alt="Get it on Google Play" className="h-10" />
        </button>
      </nav>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-6 py-12 md:py-24 flex flex-col md:flex-row items-center gap-12">
        <div className="flex-1 text-center md:text-left">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-bold mb-6">
            <Star className="w-4 h-4 fill-indigo-700" />
            The Ultimate App for WhatsApp Stickers!
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
            Create Unique WhatsApp Stickers <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">in Seconds</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
            Turn your photos and ideas into funny and professional WhatsApp stickers in seconds. The easiest and most comprehensive app.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
            <button 
              onClick={handleDownload}
              className="hover:scale-105 transition-transform w-full sm:w-auto flex justify-center"
            >
              <img src={storeBtnEn} alt="Get it on Google Play" className="h-16" />
            </button>
          </div>
        </div>
        
        {/* Mockup Image/Graphic */}
        <div className="flex-1 relative">
          <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-full blur-3xl opacity-20 animate-pulse"></div>
          <div className="relative bg-white border-[8px] border-slate-900 rounded-[3rem] shadow-2xl p-4 w-64 mx-auto md:w-80 h-[500px] flex flex-col overflow-hidden">
            <div className="w-24 h-6 bg-slate-900 absolute top-0 left-1/2 -translate-x-1/2 rounded-b-xl"></div>
            <div className="flex-1 bg-slate-50 rounded-2xl flex flex-col items-center justify-center p-6 text-center border border-slate-100">
              <Smile className="w-24 h-24 text-indigo-400 mb-6" />
              <h3 className="font-bold text-xl text-slate-800 mb-2">Make your sticker now</h3>
              <p className="text-sm text-slate-500">Choose a photo from your gallery to start</p>
              <div className="mt-8 w-full bg-indigo-600 text-white py-3 rounded-xl font-bold shadow-md">Start Designing</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-20 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">App Features in Detail ✨</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Everything you need to unleash your creativity and make unique stickers.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-blue-100 group-hover:bg-blue-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-blue-600">
                <Wand2 className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Magic Eraser 🪄</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Goodbye to tiring manual cropping! With a single click, the app's AI removes backgrounds with ultimate precision to make the image ready as a sticker.</p>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-purple-100 group-hover:bg-purple-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-purple-600">
                <Type className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Huge Fonts Library ✍️</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Write on your stickers with beautiful, professional fonts. Change text colors, add borders and shadows for clear, attractive text.</p>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-pink-100 group-hover:bg-pink-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-pink-600">
                <Smile className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Stickers & Phrases Library 😂</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Add a comedic and creative touch using a huge library of decorations, emojis, and ready-made phrases suitable for all situations.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-orange-100 group-hover:bg-orange-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-orange-600">
                <LayoutGrid className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Free & Circular Crop ✂️</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Crop images manually in circular or freeform shapes easily, with options to rotate and adjust size perfectly for chats.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-teal-100 group-hover:bg-teal-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-teal-600">
                <Package className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Unlimited Sticker Packs 📦</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Organize your stickers into custom packs (work friends pack, memes pack, family pack, etc).</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition group">
              <div className="w-14 h-14 bg-green-100 group-hover:bg-green-600 group-hover:text-white transition-colors rounded-2xl flex items-center justify-center mb-6 text-green-600">
                <Send className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Direct Add to WhatsApp 📲</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Once you finish designing, add them instantly to WhatsApp, ready to share with everyone.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-slate-900 py-20 text-center px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to make your own stickers?</h2>
        <p className="text-slate-400 mb-10 max-w-2xl mx-auto text-lg">Join thousands of users and start designing your stickers today.</p>
        <button 
          onClick={handleDownload}
          className="hover:scale-105 transition-transform inline-flex"
        >
          <img src={storeBtnEn} alt="Get it on Google Play" className="h-16" />
        </button>
      </div>
      
      {/* Footer */}
      <footer className="bg-slate-950 py-6 text-center border-t border-slate-800 text-slate-500 text-sm">
        All rights reserved © Sticker Maker Pro 2026
      </footer>
    </div>
  );
};

export default StickerMakerLandingEn;
